// @flow
/* eslint pinterest/no-data-options-dot-notation:0 */

/**
 * The default maximum number of items in the cache.
 * @type {number}
 */
const DEFAULT_MAX_ITEMS = 10;

/**
 * Linked list item. Holds data for the cache item along with next and
 * previous pointers.
 *
 * @constructor
 * @template Key, Value
 * @param {Key} uid Unique id for an item in the cache.
 * @param {Value} item The item to cache.
 */
const LruData = function(uid, item) {
  this.uid = uid;
  this.item = item;
  this.next = null;
  this.prev = null;
};

/**
 * A simple lru cache that evicts one item at a time as soon as the
 * maxNumItems is reached.
 *
 * @constructor
 * @template Key, Value
 * @param {number=} maxNumItems The maximum number of items in the cache.
 */
const LruCache = function(maxNumItems?: number) {
  /** @private */
  this._maxNumItems = maxNumItems || DEFAULT_MAX_ITEMS;

  /** @private */
  this._uidToData = {};

  /** @private */
  this._numItems = 0;

  /** @private */
  this._firstData = null;

  /** @private */
  this._lastData = null;
};

/**
 * Mark an item as being visited and sets it as the most recently used
 * item. This adds the item if it is not already in the cache and
 * evicts an item if the maximum number of items was reached.
 *
 * @param {Key} uid Unique id for an item in the cache.
 * @param {Value} item The item to cache.
 */
LruCache.prototype.set = function(uid, item) {
  let data = this._uidToData[uid];
  if (!data) {
    data = new LruData(uid, item);
    this._uidToData[uid] = data;
    this._numItems += 1;
  }
  this._moveToFront(data);
  if (this._numItems > this._maxNumItems) {
    this._evict();
  }
};

/**
 * Get an item from the cache and set the item as the most recently
 * used item.
 *
 * @param {Key} uid Unique id for an item in the cache.
 * @return {?Value} The item or null if it isn't in the cache.
 */
LruCache.prototype.get = function(uid) {
  const data = this._uidToData[uid];
  if (!data) {
    return null;
  }
  this._moveToFront(data);
  return data.item;
};

/**
 * Remove the item from the cache. Noop if the item isn't in the
 * cache.
 *
 * @param {Key} uid Unique id for an item in the cache.
 */
LruCache.prototype.remove = function(uid) {
  const data = this._uidToData[uid];
  if (data) {
    delete this._uidToData[uid];

    if (this._firstData === data) {
      this._firstData = data.next;
    }

    if (this._lastData === data) {
      this._lastData = data.prev;
    }

    if (data.prev) {
      data.prev.next = data.next;
    }
    if (data.next) {
      data.next.prev = data.prev;
    }

    this._numItems -= 1;
  }
};

/**
 * Put the data in the front of the item queue indicating that it was
 * the last item to be accessed.
 *
 * @param {LruData.<Key,Value>} data The cache data
 * @private
 */
LruCache.prototype._moveToFront = function(data) {
  if (this._firstData === data) {
    return;
  }

  if (!this._firstData) {
    this._firstData = data;
    this._lastData = data;
  } else {
    if (this._lastData === data) {
      this._lastData = data.prev;
    }
    if (data.prev) {
      data.prev.next = data.next;
    }
    if (data.next) {
      data.next.prev = data.prev;
    }
    this._firstData.prev = data;
    data.next = this._firstData;
    data.prev = null;
    this._firstData = data;
  }
};

/**
 * Remove the least recently used item.
 *
 * @private
 */
LruCache.prototype._evict = function() {
  delete this._uidToData[this._lastData.uid];
  if (this._firstData === this._lastData) {
    this._firstData = null;
    this._lastData = null;
  } else {
    this._lastData = this._lastData.prev;
    this._lastData.next = null;
  }
  this._numItems -= 1;
};

export default LruCache;
